import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Calculator, TrendingDown, ArrowRight, Info, Loader2, AlertTriangle, Target, TrendingUp, Heart, MessageCircle, Phone, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE_NUM = "7410121121";
const WHATSAPP_LINK = `https://wa.me/91${PHONE_NUM}?text=Hi,%20I%20used%20the%20Retirement%20Calculator%20on%20FutureSanchay%20and%20want%20to%20discuss%20my%20options`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const RetirementCalculator = () => {
  const [currentAge, setCurrentAge] = useState(35);
  const [retirementAge, setRetirementAge] = useState(60);
  const [monthlyNeed, setMonthlyNeed] = useState(50000);
  const [currentSavings, setCurrentSavings] = useState(500000);
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const calculateGap = async () => {
    if (currentAge >= retirementAge) {
      toast.error("Current age must be less than retirement age");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/calculator/retirement-gap`, {
        current_age: currentAge,
        retirement_age: retirementAge,
        monthly_need: monthlyNeed,
        current_savings: currentSavings,
      });
      setResult(response.data);
      toast.success("Calculation complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Calculation failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white page-transition" data-testid="retirement-calculator-page">
      <Helmet>
        <title>Retirement Gap Calculator | Are You On Track? | FutureSanchay</title>
        <meta name="description" content="Free retirement gap calculator for Indians. Find out if your savings will last through retirement. Calculate how much you need to save monthly. IRDAI expert advice available." />
        <link rel="canonical" href="https://futuresanchay.com/calculator/retirement" />
      </Helmet>
      {/* Hero Header */}
      <section className="pt-8 pb-12 md:pt-12 md:pb-16 relative overflow-hidden" data-testid="calculator-hero">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-50 rounded-full blur-3xl animate-pulse opacity-60"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-sky-50 rounded-full blur-3xl animate-pulse opacity-50" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="container-main relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 animate-fade-in-down">
            <Link to="/" className="hover:text-sky-500 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">Retirement Gap Calculator</span>
          </div>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-600 px-4 py-2 rounded-full text-sm font-medium mb-4 animate-fade-in-up">
              <Target className="w-4 h-4" />
              Free Planning Tool
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 animate-fade-in-up delay-100">
              Retirement Gap Calculator
            </h1>
            <p className="text-lg text-gray-600 animate-fade-in-up delay-200">
              Most people <span className="font-semibold text-red-500">underestimate</span> their retirement needs. Find out if you're on track and how much you need to save monthly.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="pb-12 md:pb-16">
        <div className="container-main">
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Input Form */}
            <div className="lg:col-span-7 animate-fade-in-up delay-300">
              <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-6 md:p-8 hover:shadow-2xl transition-all duration-500" data-testid="calculator-form">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Calculator className="w-5 h-5 text-emerald-500" />
                  </div>
                  Enter Your Details
                </h2>

                <div className="space-y-8">
                  {/* Current Age */}
                  <div className="group">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium text-gray-700">Your Current Age</Label>
                      <span className="font-bold text-2xl text-gray-900 group-hover:text-sky-500 transition-colors">{currentAge} years</span>
                    </div>
                    <Slider
                      value={[currentAge]}
                      onValueChange={(value) => setCurrentAge(value[0])}
                      min={25}
                      max={55}
                      step={1}
                      className="mb-2"
                      data-testid="current-age-slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>25 years</span>
                      <span>55 years</span>
                    </div>
                  </div>

                  {/* Retirement Age */}
                  <div className="group">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium text-gray-700">Desired Retirement Age</Label>
                      <span className="font-bold text-2xl text-gray-900 group-hover:text-sky-500 transition-colors">{retirementAge} years</span>
                    </div>
                    <Slider
                      value={[retirementAge]}
                      onValueChange={(value) => setRetirementAge(value[0])}
                      min={50}
                      max={70}
                      step={1}
                      className="mb-2"
                      data-testid="retirement-age-slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>50 years</span>
                      <span>70 years</span>
                    </div>
                  </div>

                  {/* Monthly Need */}
                  <div className="group">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium text-gray-700">Monthly Expenses (Today's Value)</Label>
                      <span className="font-bold text-2xl text-gray-900 group-hover:text-sky-500 transition-colors">{formatCurrency(monthlyNeed)}</span>
                    </div>
                    <Slider
                      value={[monthlyNeed]}
                      onValueChange={(value) => setMonthlyNeed(value[0])}
                      min={20000}
                      max={200000}
                      step={5000}
                      className="mb-2"
                      data-testid="monthly-need-slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>₹20,000</span>
                      <span>₹2,00,000</span>
                    </div>
                  </div>

                  {/* Current Savings */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-3 block">Current Retirement Savings</Label>
                    <Input
                      type="number"
                      value={currentSavings}
                      onChange={(e) => setCurrentSavings(Number(e.target.value))}
                      placeholder="Enter amount"
                      className="h-12 bg-white border-gray-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 rounded-xl text-base transition-all duration-300"
                      data-testid="current-savings-input"
                    />
                    <p className="text-xs text-gray-500 mt-2">Include EPF, PPF, FDs, Mutual Funds earmarked for retirement</p>
                  </div>

                  <Button
                    onClick={calculateGap}
                    disabled={isLoading}
                    className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95"
                    data-testid="calculate-btn"
                  >
                    {isLoading ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Calculating...</>
                    ) : (
                      <><Sparkles className="w-5 h-5 mr-2" /> Calculate Retirement Gap</>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-5 animate-fade-in-up delay-400">
              {result ? (
                <div className="space-y-4 animate-scale-in" data-testid="calculator-result">
                  {/* Gap Alert Card */}
                  {result.result.corpus_gap > 0 ? (
                    <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-3xl p-6 shadow-xl relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
                      <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="w-5 h-5 text-amber-300 animate-pulse" />
                          <h3 className="text-amber-200 font-medium">Retirement Gap Identified</h3>
                        </div>
                        <div className="text-4xl font-bold mb-2">
                          {formatCurrency(result.result.corpus_gap)}
                        </div>
                        <p className="text-red-100 text-sm">
                          You need to save <span className="font-bold text-white">{formatCurrency(result.result.monthly_saving_needed)}/month</span> to close this gap.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-3xl p-6 shadow-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-5 h-5" />
                        <h3 className="text-emerald-200 font-medium">You're On Track!</h3>
                      </div>
                      <div className="text-4xl font-bold mb-2">No Gap</div>
                      <p className="text-emerald-100 text-sm">Your current savings are projected to meet your retirement needs.</p>
                    </div>
                  )}

                  {/* Details Card */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-lg">
                    <h4 className="font-bold text-gray-900 mb-4">Retirement Projection</h4>
                    <div className="space-y-3">
                      {[
                        { label: "Years to Retirement", value: `${result.result.years_to_retirement} years` },
                        { label: "Future Monthly Need", value: formatCurrency(result.result.future_monthly_need) },
                        { label: "Required Corpus", value: formatCurrency(result.result.required_corpus) },
                        { label: "Projected Corpus", value: formatCurrency(result.result.projected_corpus) },
                      ].map((item, index) => (
                        <div key={index} className="flex justify-between py-2 border-b border-gray-50 last:border-0">
                          <span className="text-gray-500">{item.label}</span>
                          <span className="font-semibold text-gray-900">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Card */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-lg">
                    <h4 className="font-bold text-gray-900 mb-2">Need help closing the gap?</h4>
                    <p className="text-gray-500 text-sm mb-4">Our IRDAI certified experts can recommend suitable pension and endowment plans.</p>
                    <div className="space-y-2">
                      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="block">
                        <Button className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all hover:scale-105 animate-whatsapp-pulse" data-testid="result-whatsapp-btn">
                          <MessageCircle className="w-5 h-5 mr-2" /> Chat on WhatsApp
                        </Button>
                      </a>
                      <Link to="/contact" className="block">
                        <Button variant="outline" className="w-full h-12 rounded-xl font-semibold border-2 border-sky-200 text-sky-600 hover:bg-sky-50 hover:border-sky-300 transition-all" data-testid="result-callback-btn">
                          <Phone className="w-5 h-5 mr-2" /> Request Callback
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Disclaimer */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex gap-3">
                      <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">{result.disclaimer}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl border border-gray-100 p-8 text-center shadow-lg" data-testid="calculator-placeholder">
                  <div className="w-20 h-20 bg-emerald-100 rounded-3xl flex items-center justify-center mx-auto mb-4 animate-float">
                    <TrendingDown className="w-10 h-10 text-emerald-500" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-xl mb-2">Your Results Will Appear Here</h3>
                  <p className="text-gray-500">Enter your details to find out if you have a retirement gap.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Other Calculators */}
      <section className="pb-16 bg-gray-50">
        <div className="container-main py-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Try Other Calculators</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/calculator/monthly-saving" className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 shine-effect" data-testid="other-calc-monthly">
              <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <TrendingUp className="w-6 h-6 text-sky-500" />
              </div>
              <h4 className="font-bold text-gray-900 mb-1 group-hover:text-sky-500 transition-colors">Monthly Saving Calculator</h4>
              <p className="text-gray-500 text-sm mb-3">See how your monthly savings can grow over time.</p>
              <span className="text-sky-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                Try now <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link to="/calculator/child-education" className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 shine-effect" data-testid="other-calc-education">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Heart className="w-6 h-6 text-amber-500" />
              </div>
              <h4 className="font-bold text-gray-900 mb-1 group-hover:text-sky-500 transition-colors">Child Education Calculator</h4>
              <p className="text-gray-500 text-sm mb-3">Plan for your child's higher education.</p>
              <span className="text-sky-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                Try now <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RetirementCalculator;
